import { type MutableRefObject, useRef, useState } from "react";

import { type CheckboxChangeEvent } from "antd/lib/checkbox";

import { BuiCheckbox } from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

import {
  BdsAnalyticsEvent,
  BdsTrack,
  type IBdsTrackingProps,
  trackBdsEvent,
  useHotkey,
} from "@buildertrend/components";

import { BTPopover } from "../BTPopover/BTPopover";
import { type IBTCheckboxProps } from "./BTCheckbox.types";

export const BTCheckbox = BdsTrack({ atom: "BTCheckbox" })(function <
  FormValues = undefined,
>({
  id,
  readOnly,
  "data-testid": testid,
  hotkey,
  trackingEvent,
  tracking,
  analyticId = "BTCheckbox",
  className,
  onChange,
  disabled,
  onHotkeyPressed,
  tabIndex,
  children,
  checked,
  indeterminate,
  defaultChecked,
  tooltip,
  ...otherProps
}: IBTCheckboxProps<FormValues> & IBdsTrackingProps) {
  const localDisabled = Boolean(readOnly || disabled);

  const ref = useRef<HTMLInputElement>();

  const [isChecked, setIsChecked] = useState(
    defaultChecked ?? checked ?? false,
  );

  // Have to keep state in sync with props, this could be improved in a future iteration
  if (checked !== undefined && checked !== isChecked) {
    setIsChecked(checked ?? false);
  }
  // AntD is overriding how useRef works, so we need to use this any. 😠
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setRefCurrent = (node: any) => {
    if (node && node.input) {
      ref.current = node.input;
    } else {
      ref.current = undefined;
    }
  };
  const checkboxChangeEvent: CheckboxChangeEvent = {
    target: {
      checked: true,
    },
    nativeEvent: new MouseEvent("submit"),
    stopPropagation: () => {},
    preventDefault: () => {},
  };

  // Use this if you want to pass a different function in from the normal checkbox
  const handleHotkeyPressed = (e: CheckboxChangeEvent) => {
    if (localDisabled) {
      return;
    }

    if (onHotkeyPressed) {
      setIsChecked(e.target.checked);
      onHotkeyPressed(e);
    } else {
      handleChange(e);
    }
    trackBdsEvent(tracking, {
      element: "BTCheckbox",
      event: trackingEvent ?? BdsAnalyticsEvent.ValueChange,
      analyticId,
      testid: testid,
      extraInfo: {
        checked: e.target.checked,
      },
    });
  };

  useHotkey(
    hotkey,
    id as string,
    () => handleHotkeyPressed(checkboxChangeEvent),
    // setRefCurrent only ever stores the resolved input element here; the cast
    // drops the `| undefined` that exactOptionalPropertyTypes rejects on useHotkey's el param
    ref as MutableRefObject<HTMLInputElement>,
    undefined,
    undefined,
    undefined,
    readOnly,
  );

  const handleChange = (e: CheckboxChangeEvent) => {
    if (localDisabled) {
      return;
    }
    setIsChecked(e.target.checked);
    if (onChange) {
      onChange(e);
    }
    trackBdsEvent(tracking, {
      element: "BTCheckbox",
      event: trackingEvent ?? BdsAnalyticsEvent.ValueChange,
      analyticId,
      testid: testid,
      extraInfo: {
        checked: e.target.checked,
      },
    });
  };

  const labelId =
    id !== undefined && id !== null ? `${id as string}label` : undefined;

  return (
    <BuiCheckbox
      {...otherProps}
      className={cn("BTCheckbox", className, {
        readOnly: readOnly,
        disabled: localDisabled,
        "underline decoration-dotted cursor-pointer pointer-events-auto":
          tooltip && children,
      })}
      id={id as string}
      onChange={handleChange}
      tabIndex={localDisabled ? -1 : (tabIndex ?? 0)}
      aria-disabled={localDisabled}
      ref={setRefCurrent}
      data-testid={testid}
      indeterminate={indeterminate ?? false}
      checked={isChecked}
      defaultChecked={defaultChecked ?? false}
      // Only emit aria-labelledby when there's a real label element to point at;
      // exactOptionalPropertyTypes forbids passing undefined/null to the optional prop.
      {...(children && labelId ? { "aria-labelledby": labelId } : {})}
    >
      <BTPopover
        data-testid={testid + "-tooltip"}
        content={tooltip}
        disabled={!tooltip || !children}
      >
        {children}
      </BTPopover>
    </BuiCheckbox>
  );
});
